import { ISigner } from '@/types'
import { normalizeUrl } from '@/lib/url'
import { pool } from '@nostr/gadgets/global'
import { AbstractRelay } from '@nostr/tools/abstract-relay'
import { EventTemplate, VerifiedEvent } from '@nostr/tools/core'

// AbstractRelay#auth() caches its promise for the whole connection and swallows
// whatever the sign callback throws. One rejected -- or simply never answered --
// sign request is therefore enough to poison that relay: every later auth()
// hands back the same dead promise and everything that needs signing hangs.
// So we bound the sign request and clear the cached promise, letting the next
// attempt sign again.
const AUTH_SIGN_TIMEOUT = 15_000
const AUTH_TOTAL_TIMEOUT = AUTH_SIGN_TIMEOUT + 15_000

function withTimeout<T>(promise: Promise<T>, ms: number, message: string): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(() => reject(new Error(message)), ms)
  })
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer))
}

function findRelay(url: string | undefined): AbstractRelay | undefined {
  if (!url) return undefined
  const relays = (pool as unknown as { relays?: Map<string, AbstractRelay> }).relays
  return relays?.get(normalizeUrl(url))
}

function clearCachedAuth(relay: AbstractRelay | undefined) {
  if (!relay) return
  ;(relay as unknown as { authPromise?: unknown }).authPromise = undefined
}

function relayUrlFromAuthEvent(authEvt: EventTemplate): string | undefined {
  return authEvt.tags.find(([tagName]) => tagName === 'relay')?.[1]
}

export async function signAuthEvent(
  authEvt: EventTemplate,
  signer: ISigner,
  onSignFailure?: (err: unknown) => void
): Promise<VerifiedEvent> {
  try {
    return await withTimeout(signer.signEvent(authEvt), AUTH_SIGN_TIMEOUT, 'auth sign timed out')
  } catch (err) {
    clearCachedAuth(findRelay(relayUrlFromAuthEvent(authEvt)))
    onSignFailure?.(err)
    throw err
  }
}

export async function authRelay(relay: AbstractRelay, signer: ISigner): Promise<string> {
  let onSignFailure: (err: unknown) => void = () => undefined
  const signFailure = new Promise<never>((_, reject) => {
    onSignFailure = reject
  })

  try {
    return await withTimeout(
      Promise.race([
        relay.auth((authEvt) => signAuthEvent(authEvt, signer, onSignFailure)),
        signFailure
      ]),
      AUTH_TOTAL_TIMEOUT,
      'relay auth timed out'
    )
  } catch (err) {
    // a connection is only good for one challenge, so the only way to get
    // another one is to let the pool build a fresh relay
    clearCachedAuth(relay)
    relay.close()
    throw err
  }
}
